<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      search-container-class="d-flex justify-content-end align-items-start"
      search-text=""
      @academicYear="onChangeAcademicYear"
      @period="onChangePeriod"
      @provider="onChangeProvider"
      @specialist="onChangeSpecialist"
    >
      <div class="col-12 col-md-3 d-flex align-items-start mt-4">
        <ActionButton
          class="search mb-2 col-12 col-md-8 offset-md-4 mt-2"
          i="fas fa-search"
          text="Buscar"
          variant="success"
          @click="onSearch"
        />
        <input
          id="inputFileForUploadingMassiveCertifications"
          ref="uploadFiles"
          accept=".pdf"
          multiple
          style="display: none"
          type="file"
          @change="showCertificationModal"
        />
      </div>
    </filter-manager>
    <custom-separator class="mt-1 mb-4" text="Resultado de Búsqueda" />
    <custom-result-not-found
      v-if="!results && results.details.length == 0"
      :text="'No hay información disponible.'"
    />
    <template v-else>
      <CertificationResult
        :data="results"
        :file-error-data="fileErrorData"
        :on-refresh="onSearch"
        :pagination="pagination"
        @go-to-page-handler="goToPageHandler"
      />
    </template>
    <custom-modal
      :cancel-icon="'fas fa-times-circle'"
      :cancel-text="'Cerrar'"
      fit-height-content
      icon="fas fa-exclamation-circle"
      is-centered
      :save-icon="''"
      :save-text="'Aceptar'"
      show-cancel
      show-save
      :title="'Certificación'"
      :value="showModal"
      :width="'740px'"
      @cancel="onCancelAcceptTermHandler"
      @save="onAcceptTermHandler"
    >
      <custom-checkbox-single-option
        v-model="acceptedTerms"
        class="font-weight-bold"
        :option-text="'Acepto los términos y responsabilidades'"
        :title="
          'Certifico que este documento que he subido al Sistema REMpro es copia fiel y exacta del original que pertenece a los récords de la Unidad Secretarial del Procedimiento de Querellas y Remedio Provisional (USPQRP) y que como proveedor de servicios seré custodio del mismo. Reconozco y acepto que este documento original me puede ser solicitado por la USPQRP en cualquier momento.(0423)'
        "
      />
    </custom-modal>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import ActionButton from 'ActionButton';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import CustomModal from 'CustomModal';
import CustomCheckboxSingleOption from 'custom-checkbox-single-option';
import {
  getInitFilters as _getInitFilters,
  getDisciplineService as _getDisciplineService,
  getActiveProviders as _getActiveProviders,
  getActiveSpecialistsByProvider as _getActiveSpecialistsByProvider,
  search as _search,
  uploadCertificationFiles as _uploadCertificationFiles,
} from '@/services/provider/services-month-certification.api.js';
import { getMonthsInAcademicYear as _getMonthsInAcademicYear } from '@/services/provider/billing-history.api.js';
import { getSpecialistByUserId as _getSpecialistByUserId } from '@/services/provider/specialist.api';
import CertificationResult from './components/CertificationResult.vue';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import billingStatus from '@/utils/constants/billingStatus';
import roles from '@/utils/constants/roles.js';
import { mapGetters } from 'vuex';

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'ServicesMonthCertificationManagementMain',
  mixins: [alertMixin],
  components: {
    PageHeader,
    FilterManager,
    ActionButton,
    CustomSeparator,
    CertificationResult,
    CustomResultNotFound,
    CustomModal,
    CustomCheckboxSingleOption,
  },
  data: () => ({
    filterModel: {
      academicYear: null,
      period: null,
      provider: null,
      specialist: null,
      disciplineService: null,
      status: null,
      SIENumber: null,
    },
    filterOptions: {
      academicYears: [],
      periods: [],
      providers: [],
      specialists: [],
      disciplineServices: [],
      statuses: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: {},
    fileErrorData: {
      validations: [],
      showAlert: false,
      failedUploadsCount: 0,
    },
    showModal: false,
    acceptedTerms: false,
    selectedFile: [],
  }),
  computed: {
    ...mapGetters('$_user', ['fullProfile', 'companies']),
    headerConfig() {
      return {
        title: 'Certificaciones de Servicios Mensuales',
        icon: 'fas fa-user-tie',
        routes: [
          'Inicio',
          'Proveedor',
          'Certificaciones de Servicios Mensuales',
        ],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Cargar Documentos',
          icon: 'fas fa-file-plus pr-1',
          variant: 'success',
          click: () => this.onShowUploadFiles(),
          visible: this.canUploadDocuments,
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          label: 'Año Académico:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: false,
          validations: 'required',
          error: 'El año académico es requerido.',
        },
        {
          name: 'period',
          component: 'CustomDropDown',
          options: this.filterOptions.periods,
          label: 'Periodo:',
          placeholder: 'Seleccione',
          fieldName: 'monthDesc',
          fieldKey: 'monthNumber',
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El periodo es requerido.',
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          label: 'Proveedor:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El proveedor es requerido.',
          disabled: this.disableProviderDropdown,
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          label: 'Especialista:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El especialista es requerido.',
        },
        {
          name: 'disciplineService',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplineServices,
          label: 'Servicio:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          label: 'Estatus:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'SIENumber',
          component: 'CustomInput',
          label: 'Número de SIE:',
          placeholder: 'Escriba Número de SIE',
          value: this.filterModel.SIENumber,
          class: 'col-12 col-md-3',
        },
      ];
    },
    disableProviderDropdown() {
      return (
        this.userCompanies?.length === 1 && this.filterModel.provider != null
      );
    },
    canUploadDocuments() {
      const data = this.results?.details?.data;

      const pendingDocuments =
        (this.results?.header?.pendingDocuments || 0) > 0;
      const allFinalized = data?.every((x) => x.finalized);

      return pendingDocuments && allFinalized;
    },
    isProviderRp() {
      return this.fullProfile?.roleid?.toUpperCase() === roles.Provider_RP;
    },
    isSpecialistRp() {
      return this.fullProfile?.roleid?.toUpperCase() === roles.Specialist_RP;
    },
  },
  async mounted() {
    await this.getInitFilters();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (await _getInitFilters()).data;
      this.filterModel = {
        ...this.filterModel,
        ...filters,
      };
      this.filterOptions = {
        ...this.filterOptions,
        ...collections,
      };
      this.onChangeAcademicYear(this.filterModel.academicYear);
    },
    async onChangeAcademicYear(value) {
      if (value?.id) {
        const { data } = await _getMonthsInAcademicYear(value.id);
        this.filterOptions.periods = data;
        this.filterModel.period = null;

        this.filterOptions.providers = (
          await _getActiveProviders(
            value.id,
            this.filterModel.period?.monthNumber
          )
        ).data;
        this.filterModel.provider = null;

        this.onChangePeriod();

        if (this.isProviderRp || this.isSpecialistRp) {
          if (this.companies?.length === 1) {
            const { id, name } = this.companies[0];
            const currentCompanyIsSelectable = this.filterOptions.providers.find(
              (provider) => provider.id == id
            );

            let selectedProvider = {
              id,
              name,
              disabled: true,
              sortOrder: 0,
              isDirty: false,
              selected: true,
              keyId: '00000000-0000-0000-0000-000000000000',
            };

            this.filterOptions.providers = currentCompanyIsSelectable
              ? [selectedProvider]
              : [];
            this.filterModel.provider = currentCompanyIsSelectable
              ? selectedProvider
              : null;
          } else {
            this.filterOptions.providers = this.companies;
          }
        }

        if (this.isSpecialistRp) {
          const { data } = await _getSpecialistByUserId();
          let selectedSpecialist = {
            id: data.id,
            name: data.name,
            disabled: true,
            sortOrder: 0,
            isDirty: false,
            selected: true,
            keyId: '00000000-0000-0000-0000-000000000000',
          };

          this.filterOptions.specialists = [selectedSpecialist];
          this.filterModel.specialist = selectedSpecialist;
        }
      }
    },
    async onChangePeriod() {
      if (!this.isProviderRp && !this.isSpecialistRp) {
        this.filterModel.provider = null;
        this.filterOptions.providers = (
          await _getActiveProviders(
            this.filterModel.academicYear.id,
            this.filterModel.period?.monthNumber
          )
        ).data;
      } else if (this.filterModel.period?.monthNumber == 7) {
        const { id, name } = this.companies[0];
        this.filterOptions.providers = (
          await _getActiveProviders(
            this.filterModel.academicYear.id,
            this.filterModel.period?.monthNumber
          )
        ).data;

        const currentCompanyIsSelectable = this.filterOptions.providers.find(
          (provider) => provider.id == id
        );
        let selectedProvider = {
          id,
          name,
          disabled: true,
          sortOrder: 0,
          isDirty: false,
          selected: true,
          keyId: '00000000-0000-0000-0000-000000000000',
        };
        this.filterOptions.providers = currentCompanyIsSelectable
          ? [selectedProvider]
          : [];
        this.filterModel.provider = currentCompanyIsSelectable
          ? selectedProvider
          : null;
      }
      this.onChangeProvider(this.filterModel.provider);
    },
    async onChangeProvider(value) {
      if (!this.isSpecialistRp) {
        this.filterModel.specialist = null;
      }

      this.filterOptions.specialists = [];
      if (value?.id && this.filterModel.period?.monthNumber) {
        const academicYearId = this.filterModel.academicYear.id;
        const monthNumber = this.filterModel.period?.monthNumber;
        this.filterOptions.specialists = (
          await _getActiveSpecialistsByProvider(
            academicYearId,
            monthNumber,
            value.id
          )
        ).data;
      }
      this.onChangeSpecialist(this.filterModel.specialist);
    },
    async onChangeSpecialist(value) {
      this.filterModel.disciplineService = null;
      this.filterOptions.disciplineServices = [];
      if (
        value?.id &&
        this.filterModel.academicYear?.id &&
        this.filterModel.period?.monthNumber &&
        this.filterModel.provider?.id
      ) {
        const academicYearId = this.filterModel.academicYear.id;
        const periodId = this.filterModel.period.monthNumber;
        const providerId = this.filterModel.provider.id;
        this.filterOptions.disciplineServices = (
          await _getDisciplineService(
            academicYearId,
            periodId,
            providerId,
            value.id
          )
        ).data;
      }
    },
    fillPayload(page) {
      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        monthNumber: this.filterModel.period?.monthNumber,
        companyId: this.filterModel.provider?.id,
        specialistId: this.filterModel.specialist?.id,
        disciplineTypeId: this.filterModel.disciplineService?.id,
        status: this.filterModel.status?.id,
        sieNumber: this.filterModel.SIENumber,
        pageIndex: page ? page : 1,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
    async onSearch() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();

      if (!isValid) {
        await this.$refs.filterRef.$refs.formValidator.reset();
        await this.$refs.filterRef.$refs.formValidator.validate();
        this.ShowToast('Error', 'Información Incompleta', 'error');
        return;
      }
      const payload = this.fillPayload(1);
      this.searchEndTerm(payload);
      this.clearErrorValidations();
    },
    async searchEndTerm(payload) {
      const { data } = await _search(payload);
      const dataResults = data.details;

      this.results = {
        ...data,
        details: {
          ...data.details,
          data: [...dataResults],
        },
      };

      this.pagination = {
        ...this.pagination,
        count: dataResults.count,
        currentPage: dataResults.currentPage,
        pageCount: dataResults.pageCount,
        totalItemCount: dataResults.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      const payload = this.fillPayload(page);
      await this.searchEndTerm(payload);
    },
    onShowUploadFiles() {
      this.$refs.uploadFiles.click();
    },
    clearErrorValidations() {
      this.fileErrorData = {
        validations: [],
        showAlert: false,
        failedUploadsCount: 0,
      };
    },
    async onUploadFiles(files) {
      if (files.length > 0 && this.results?.details?.data?.length > 0) {
        const selectedFiles = files;
        const pendingDocuments = this.results.details.data.filter(
          (item) =>
            item.billingStatusId === billingStatus.Preliminar ||
            item.billingStatusId === billingStatus.NoExiste ||
            item.bill == undefined ||
            item.bill == null
        );
        let data = pendingDocuments.map((item) => {
          const matchedFileIndex = selectedFiles.findIndex(
            (file) => file.name.split('.').shift() == item.contractNumber
          );
          const matchedFile = selectedFiles[matchedFileIndex];
          if (matchedFileIndex > -1) {
            selectedFiles.splice(matchedFileIndex, 1);

            return {
              file: matchedFile ?? null,
              disciplineTypeId: item.disciplineTypeId,
              visitSummaryId: item.visitSummaryId,
              studentSieId: item.studentSieId,
              contractId: item.contractId,
              contractNumber: item.contractNumber,
            };
          }
        });
        data = data.filter((item) => item != undefined);
        let formData = new FormData();
        data.forEach((item, index) => {
          formData.append(`files[${index}].contractId`, item.contractId);
          formData.append(
            `files[${index}].visitSummaryId`,
            item.visitSummaryId
          );
          formData.append(
            `files[${index}].disciplineTypeId`,
            item.disciplineTypeId
          );
          formData.append(`files[${index}].studentSieId`, item.studentSieId);
          formData.append(
            `files[${index}].contractNumber`,
            item.contractNumber
          );
          formData.append(`files[${index}].file`, item.file);
        });

        await _uploadCertificationFiles(formData)
          .then((res) => {
            this.$refs.uploadFiles.value = null;

            if (res?.data) {
              if (res.data?.errorMessages?.length > 0) {
                this.fileErrorData.validations = res.data.errorMessages;
                this.fileErrorData.showAlert = true;
                this.fileErrorData.failedUploadsCount = res.data.failedUploads;
                return;
              }

              if (res.data?.totalSucceed > 0) {
                const successMessage =
                  res.data?.totalSucceed === 1
                    ? 'Se ha cargado el documento exitosamente.'
                    : 'Se han cargado los documentos exitosamente.';

                this.ShowToast('Proceso Exitoso', successMessage, 'success');
              }
            }

            this.onSearch();
          })
          .catch((err) =>
            this.ShowToast('Alerta', err.response.data.message, 'error')
          );
      }
    },
    onCancelAcceptTermHandler() {
      this.showModal = false;
      this.acceptedTerms = false;
      this.selectedFile = [];
    },
    onAcceptTermHandler() {
      if (!this.acceptedTerms) {
        this.ShowToast(
          'Alerta',
          'Favor certifique el documento  que ha subido al sistema REMpro.',
          'error'
        );
        return;
      }
      this.showModal = false;
      this.acceptedTerms = false;
      this.onUploadFiles(this.selectedFile);
    },
    showCertificationModal(e) {
      if (e.target.files && e.target.files.length > 0) {
        this.showModal = true;
        this.selectedFile = Array.from(e.target.files)?.map((file) => file);
      }
    },
  },
};
</script>
